import { Button, NumberInput } from '@carbon/react';
import { Add } from '@carbon/icons-react';
import { asProblem, FormModal, hasPermission, problemMessage, useAdmin, type RecordValues } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { hoApi } from '../api';
import { TransientNotice } from '../common/Notices';

const MAX_CARDS = 1000;

/**
 * Card stock for a club (ADR 0044): free cards from its series, to print and hand out. Each is assigned later, at head office
 * or when the guest is enrolled at the till with it.
 */
export function GenerateCardsButton({ club }: { club: RecordValues }) {
  const { t } = useTranslation();
  const { session } = useAdmin();
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState<number>();
  const [made, setMade] = useState<string>();

  const generate = useMutation({
    mutationFn: async (count: number) => {
      const { data, error } = await hoApi.POST('/api/v1/membership/clubs/{clubId}/cards/generate', {
        params: { path: { clubId: String(club.id) } },
        body: { quantity: count },
      });
      if (!data) throw error;
      return data;
    },
    onSuccess: (result) => {
      setQuantity(undefined);
      setMade(t('loyalty.cards.made', { count: Number(result.count), first: result.first, last: result.last }));
      void queryClient.invalidateQueries();
    },
  });

  if (!hasPermission(session, 'ho.members.cards.manage')) return null;
  const valid = quantity !== undefined && Number.isInteger(quantity) && quantity >= 1 && quantity <= MAX_CARDS;
  return (
    <>
      {made && <TransientNotice kind="success" title={made} onDone={() => setMade(undefined)} />}
      <Button kind="tertiary" renderIcon={Add} onClick={() => setQuantity(100)}>
        {t('loyalty.cards.generate')}
      </Button>
      {quantity !== undefined && (
        <FormModal
          title={t('loyalty.cards.generate')}
          label={String(club.name ?? '')}
          primaryText={t('loyalty.cards.generateSave')}
          disabled={!valid}
          pending={generate.isPending}
          error={generate.error ? problemMessage(t, asProblem(generate.error)) : null}
          onClose={() => setQuantity(undefined)}
          onSubmit={() => valid && generate.mutate(quantity)}
        >
          <NumberInput
            id="loyalty-cards-quantity"
            label={t('loyalty.cards.quantity')}
            helperText={t('loyalty.cards.quantityHelp')}
            min={1}
            max={MAX_CARDS}
            step={1}
            value={quantity}
            onChange={(_, { value }) => setQuantity(Number(value) || 0)}
          />
        </FormModal>
      )}
    </>
  );
}
